import {type FC, useCallback, useState} from 'react';
import {contextMenu} from 'react-contexify';
import {useNavigate} from 'react-router';

import {Observer} from 'mobx-react';

import type {TrackersAction} from '@yourcoach/shared/modules/trackers/client/stores/Trackers/types';
import type {GetComponentProps} from '@yourcoach/shared/utils/utility-types';

import CalendarSvg from '@yourcoach/shared/assets/icons/secondary/Calendar.svg';
import {ToolboxBanner} from '@yourcoach/shared/components/ToolboxBanner';
import {TrackersContainer} from '@yourcoach/shared/modules/trackers/client/ui/containers/Trackers/TrackersContainer';
import {Clickable} from '@yourcoach/shared/uikit/Clickable';
import {Icon} from '@yourcoach/shared/uikit/Icon';
import {Text} from '@yourcoach/shared/uikit/Text';
import {logger} from '@yourcoach/shared/utils/logger';

import {routerPath} from '@src/routes/path';
import {ContextMenu} from '@src/v2/components/ContextMenu';
import DatePicker from '@src/v2/components/DatePicker';
import {PageContainer} from '@src/v2/containers/Page';

import * as S from './styles';

const CONTEXT_MENU_ID = 'cm.trackers.more';

export const TrackersPage: FC = () => {
  const navigate = useNavigate();
  const [menuActions, setMenuActions] = useState<TrackersAction[]>([]);

  const onFoodDiaryClick = useCallback(() => {
    logger.event('food_diary_tap', {
      screen: 'trackers',
    });

    navigate(routerPath.foodDiary.$buildPath({}));
  }, [navigate]);

  const onMoreIconClick = useCallback<
    GetComponentProps<typeof TrackersContainer>['onMoreIconClick']
  >((e, _context, actions) => {
    if (actions.length === 0) {
      return;
    }

    setMenuActions(actions);

    if (e.event.DOMEvent) {
      contextMenu.show({event: e.event.DOMEvent, id: CONTEXT_MENU_ID});
    }
  }, []);

  const renderCurrentDate: GetComponentProps<
    typeof TrackersContainer
  >['renderCurrentDate'] = ({store}) => (
    <Observer>
      {() => (
        <DatePicker
          customInput={
            <S.DateContainer>
              <Icon color="icon.gray" svg={CalendarSvg} />
              <Clickable>
                <Text color="text.gray">{store.selectedDate.format('ll')}</Text>
              </Clickable>
            </S.DateContainer>
          }
          maxDate={new Date()}
          onChange={date => {
            if (date) {
              store.setSelectedDate(date.valueOf());
            }
          }}
        />
      )}
    </Observer>
  );

  return (
    <PageContainer isWithBreadcrumbs isWithHeader={false}>
      <S.Container>
        <S.Header>
          <ToolboxBanner variant="trackers" />
          <ToolboxBanner
            onClick={onFoodDiaryClick}
            size="sm"
            variant="food_diary"
          />
        </S.Header>
        <TrackersContainer
          onMoreIconClick={onMoreIconClick}
          renderCurrentDate={renderCurrentDate}
        />
      </S.Container>
      <ContextMenu.Menu animation="fade" id={CONTEXT_MENU_ID}>
        {menuActions.map(action => {
          const IconComponent = action.icon;

          return (
            <ContextMenu.Item
              data={action}
              disabled={action.disabled}
              key={action.id}
              onClick={action.exec}
            >
              <S.ContainerItem>
                <IconComponent />
                <S.ContainerActions>
                  <S.Title>{action.title}</S.Title>
                  <S.Description>{action.text}</S.Description>
                </S.ContainerActions>
              </S.ContainerItem>
            </ContextMenu.Item>
          );
        })}
      </ContextMenu.Menu>
    </PageContainer>
  );
};
